import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService, LoginRequest } from '../../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { CommonService } from '../../services/common.service';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    MatAngularModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: true,
})
export class LoginComponent implements OnInit {
  fb = inject(FormBuilder);
  router = inject(Router);
  authService = inject(AuthService);
  snackBar = inject(MatSnackBar);
  errorHandler = inject(ErrorHandlerService);
  commonService = inject(CommonService);

  loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });

  isLoading = false;

  constructor() { }

  ngOnInit() {
    // Check if user is already authenticated
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/common-ai-analysis/education/university']);
    }

    // Example: Access current flow state
    const currentFlow = this.commonService.getCurrentFlow();
    // console.log('Current flow in login:', currentFlow ? 'Generic' : 'Education');

    // Example: Check flow type
    if (this.commonService.isGenericFlowActive()) {
      // console.log('Using Generic Flow');
    } else {
      // console.log('Using Education Flow');
    }
  }

  async login() {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;

    const request: LoginRequest = {
      email: this.loginForm.value.email,
      password: this.loginForm.value.password
    };

    try {
      await this.authService.login(request).toPromise();
      this.snackBar.open('Login successful! Welcome back.', 'Close', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
      if(this.commonService.isGenericFlowActive()) {
        this.router.navigate(['/generic-dashboard']);
      } else {
        this.router.navigate(['/common-ai-analysis/education/university']);
      }
      // this.router.navigate(['/common-ai-analysis/education/university']);
    } catch (error: any) {
      const errorMessage = this.errorHandler.extractErrorMessage(
        error,
        'Login failed. Please check your credentials and try again.'
      );

      this.snackBar.open(errorMessage, 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    } finally {
      this.isLoading = false;
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.loginForm.get(controlName);

    if (control?.hasError('required')) {
      return `${this.getFieldLabel(controlName)} is required`;
    }

    if (control?.hasError('email')) {
      return 'Please enter a valid email address';
    }

    return '';
  }

  private getFieldLabel(controlName: string): string {
    const labels: { [key: string]: string } = {
      email: 'Email',
      password: 'Password'
    };
    return labels[controlName] || controlName;
  }
}
